import { datadogRum } from '@datadog/browser-rum';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

import { AUTH_ERROR_CODES } from '@/consts/auth-errors';
import { isInvalidAccessToken, withInvalidAccessTokenMessage } from '@/lib/invalid-access-token';

/**
 * 401 응답 시 호출될 글로벌 콜백
 * GrsSessionProvider에서 등록하고, AppApi에서 호출한다.
 */
let onUnauthorizedCallback: (() => void) | null = null;

export function setOnUnauthorized(callback: (() => void) | null) {
  onUnauthorizedCallback = callback;
}

export class AppApi {
  url: string;
  INSTANCE: AxiosInstance;
  token: string = '';

  constructor(url: string) {
    this.url = url;
    this.INSTANCE = axios.create({
      baseURL: this.url,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
  setup(token: string) {
    this.INSTANCE.defaults.headers.common.Authorization = `Bearer ${token}`;

    this.INSTANCE.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        const status = error.response?.status;

        if (error.response && isInvalidAccessToken(status, error.response.data)) {
          error.response.data = withInvalidAccessTokenMessage(
            error.response.data as Record<string, unknown>,
          );
        }

        // 약관(서비스이용/개인정보) 미동의 403 → 약관 동의 게이트로 유도
        // BE(add-term-guard) enforce 모드에서 error 코드로 내려옴.
        // ⚠️ GRS 콜백(토큰 revalidate)보다 먼저 처리 — GRS 테넌트에서 갱신 루프 방지.
        if (
          status === 403 &&
          (error.response?.data as { error?: string } | undefined)?.error ===
            AUTH_ERROR_CODES.TERMS_AGREEMENT_REQUIRED
        ) {
          if (typeof window !== 'undefined') {
            const path = window.location.pathname;
            // 루프 가드: 이미 게이트/가입 플로우면 리다이렉트하지 않음
            if (!path.startsWith('/gateway') && !path.startsWith('/auth/sign-up')) {
              window.location.replace('/gateway');
            }
          }
          return Promise.reject(error);
        }

        // 401/403 → GrsSessionProvider revalidate 트리거
        if ((status === 401 || status === 403) && onUnauthorizedCallback) {
          onUnauthorizedCallback();
        }

        // Kakao 유저의 401/403을 Datadog에 명시적 에러로 기록
        // (RUM explorer에서 @error.message:"Auth failed*" 로 검색 가능)
        if (status === 401 || status === 403) {
          datadogRum.addError(new Error(`Auth failed: ${status}`), {
            url: error.config?.url,
            method: error.config?.method,
            status,
            responseBody: error.response?.data,
          });
        }

        if (status && status >= 500) {
          datadogRum.addError(error, {
            url: error.config?.url,
            method: error.config?.method,
            status,
          });
        }

        return Promise.reject(error);
      },
    );

    return this;
  }

  setupMateToken(mateToken: string) {
    this.INSTANCE.defaults.headers.common['mate-magic-token'] = mateToken;
    return this;
  }

  setupPublic() {
    delete this.INSTANCE.defaults.headers.common.Authorization;
    return this;
  }
}

export default AppApi;
